import React from 'react';
import { motion } from 'framer-motion';
import '../styles/_services.scss';
import { 
  FaWheelchair, 
  FaWalking, 
  FaBolt,
  FaHeartbeat, 
  FaFlask, 
  FaDumbbell, 
  FaUserMd, 
  FaHospital, 
  FaBuilding, 
  FaStethoscope, 
  FaXRay
} from 'react-icons/fa';

const Services = ({ onContactClick, onBookClick }) => {
  const modesOfTransport = [
    {
      icon: <FaWalking size={40} />,
      title: "Ambulatory Transport",
      description: "For patients who can walk independently or with minimal assistance. Our professional drivers provide door-to-door service with a helping hand when needed, ensuring a comfortable journey to your medical appointments."
    },
    {
      icon: <FaWheelchair size={40} />,
      title: "Wheelchair Accessible Transport",
      description: "Safe and secure transportation for passengers requiring wheelchair accessibility. Our ADA-compliant vehicles are equipped with proper wheelchair securement systems, and our trained drivers ensure proper assistance throughout your journey."
    },
    {
      icon: <FaBolt size={40} />,
      title: "Electric Wheelchair Accessible Transport",
      description: "Specialized transportation for individuals using electric wheelchairs or power mobility devices. Our vehicles and drivers are equipped to handle larger and heavier equipment with care, ensuring safe and secure transport for all power mobility needs."
    }
  ];

  const ongoingServices = [
    {
      icon: <FaHeartbeat size={40} />,
      title: "Dialysis Transportation",
      description: "Reliable, ongoing transportation to and from dialysis treatments. We understand the critical importance of consistent dialysis appointments and work with you to establish a regular schedule that ensures you never miss a treatment."
    },
    {
      icon: <FaFlask size={40} />,
      title: "Chemotherapy & Radiation Transportation",
      description: "Compassionate transportation for cancer treatment appointments. We provide comfortable, stress-free rides to chemotherapy and radiation therapy sessions, recognizing that your focus should be on healing, not transportation logistics."
    },
    {
      icon: <FaDumbbell size={40} />,
      title: "Physical Therapy Transportation",
      description: "Regular transportation to physical therapy and rehabilitation appointments. Our consistent service helps you maintain your therapy schedule, supporting your recovery journey with reliable, on-time transportation."
    },
    {
      icon: <FaUserMd size={40} />,
      title: "Regular Medical Appointments",
      description: "Ongoing transportation for routine medical appointments, specialist visits, and follow-up care. We work with you to establish a recurring schedule that fits your healthcare needs and ensures continuity of care."
    }
  ];

  const oneTimeServices = [
    {
      icon: <FaStethoscope size={40} />,
      title: "Doctor Appointments",
      description: "Transportation to primary care physicians, specialists, and medical consultations. Whether it's a routine check-up or a specialist visit, we ensure you arrive on time and comfortably."
    },
    {
      icon: <FaHospital size={40} />,
      title: "Hospital Discharge",
      description: "Safe transport home from hospitals and rehabilitation centers. We coordinate with healthcare facilities to ensure a smooth transition, helping you get home safely after your hospital stay."
    },
    {
      icon: <FaBuilding size={40} />,
      title: "Facility Transfers",
      description: "Transportation between medical facilities, rehabilitation centers, assisted living communities, and nursing homes. We handle the logistics so you can focus on your care transition."
    },
    {
      icon: <FaUserMd size={40} />,
      title: "Outpatient Surgery",
      description: "Transportation to and from outpatient surgical procedures. We provide reliable pre-surgery transport and comfortable post-surgery rides home, ensuring you have the support you need throughout your procedure day."
    },
    {
      icon: <FaXRay size={40} />,
      title: "Diagnostic Testing",
      description: "Transportation to imaging centers, laboratories, and diagnostic facilities for X-rays, MRIs, CT scans, blood work, and other medical tests. We ensure you arrive on time for your scheduled appointments."
    }
  ];

  return (
    <div>
      <div className="services-hero">
        <h1>Non-Emergency Medical Transportation</h1>
        <p>Professional NEMT services for all your medical transportation needs</p>
      </div>

      {/* Modes of Transport */}
      <section className="services-section bg-light">
        <div className="services-section-container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Modes of Transport
          </motion.h2>
          <p className="section-description">
            We offer various transportation modes to accommodate different mobility needs and medical requirements.
          </p>
          <div className="services-grid">
            {modesOfTransport.map((service, index) => (
              <motion.div
                className="service-card"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="service-icon">{service.icon}</div>
                <h2>{service.title}</h2>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ongoing Transportation Services */}
      <section className="services-section bg-white">
        <div className="services-section-container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Ongoing Transportation Services
          </motion.h2>
          <p className="section-description">
            We provide reliable, recurring transportation for regular medical appointments and treatments. Our ongoing service ensures consistency and peace of mind for your healthcare journey.
          </p>
          <div className="services-grid">
            {ongoingServices.map((service, index) => (
              <motion.div
                className="service-card"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="service-icon">{service.icon}</div>
                <h2>{service.title}</h2>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* On-Time Transportation Services */}
      <section className="services-section bg-light">
        <div className="services-section-container">
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            On-Time Transportation Services
          </motion.h2>
          <p className="section-description">
            For single medical appointments, procedures, and healthcare-related transportation needs, we provide flexible on-time NEMT services tailored to your specific medical requirements.
          </p>
          <div className="services-grid">
            {oneTimeServices.map((service, index) => (
              <motion.div
                className="service-card"
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="service-icon">{service.icon}</div>
                <h2>{service.title}</h2>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta bg-primary">
        <div className="services-section-container">
          <h2>Ready to Book Your Ride?</h2>
          <p>Contact us today to schedule your transportation or request a quote</p>
          <div className="cta-buttons">
            <button onClick={(e) => onContactClick && onContactClick(e)} className="primary-button">Contact Us</button>
            <a href="tel:3604823996" className="secondary-button">Call (360) 482-3996</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
